import * as Sentry from '@sentry/browser';
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';

import makeApiRequest from '../../utils/makeApiRequest';
import { track } from '../../utils/tracking';

import { API_ENDPOINTS } from '../../constants';
import ProgressBar from './ProgressBar';
import StepBegin from './StepBegin';
import StepFinished from './StepFinished';
import StepInstall from './StepInstall';
import StepInstantActivation from './StepInstantActivation';
import StepNumber from './StepNumber';
import StepCard from './StepCard';
import StepNumberTransfer from './StepNumberTransfer';
import StepNumberTransferIssue from './StepNumberTransferIssue';
import StepTransferStarted from './StepTransferStarted';

const Steps = {
  START: 10,
  NUMBER: 30,
  NUMBER_TRANSFER: 44,
  NUMBER_TRANSFER_STARTED: 46,
  NUMBER_TRANSFER_ISSUE: 48,
  CARD: 64,
  INSTALL: 80,
  FINISHED: 90,
};

const Activation = ({ activationCode, instantActivation }) => {
  const [customerId, setCustomerId] = useState();
  const [phoneNumber, setPhoneNumber] = useState();
  const [provisionData, setProvisionData] = useState();
  const [portinError, setPortinError] = useState();
  const [orderId, setOrderId] = useState();
  const [networkInstructions, setNetworkInstructions] = useState();
  const [step, setStep] = useState(Steps.START);
  const [progressStep, setProgressStep] = useState(1);
  const [count] = useState(4);
  const [fetchingNumber, setFetchingNumber] = useState(false);
  const [transferError, setTransferError] = useState();
  const [rules, setRules] = useState();
  const [status, setStatus] = useState({
    cardVaulted: false,
    portinSubmitted: false,
    provisioned: false,
  });

  // Scroll to top on step change
  useEffect(() => {
    window.scrollTo(0, 0, 'smooth');
  }, [step]);

  // Update progresbar
  useEffect(() => {
    if (step === Steps.START) {
      setProgressStep(1);
    } else if ([Steps.NUMBER, Steps.NUMBER_TRANSFER, Steps.NUMBER_TRANSFER_STARTED, Steps.NUMBER_TRANSFER_ISSUE].includes(step)) {
      setProgressStep(2);
    } else if ([Steps.CARD].includes(step)) {
      setProgressStep(3);
    } else if (step === Steps.FINISHED) {
      setProgressStep(4);
    }
  }, [step]);

  // Poll for activation number
  useEffect(() => {
    const delays = [5000, 15000, 30000, 60000];
    let timeoutId;

    if (customerId && step > Steps.ACCOUNT && !fetchingNumber && !phoneNumber) {
      let currentAttempt = 0;

      const pollForNumber = async () => {
        setFetchingNumber(true);
        try {
          const data = await makeApiRequest(API_ENDPOINTS.ACTIVATION_BEGIN, {
            activationCode: orderId,
          });

          const stepWithNumber = data.Steps.find((step) => step?.StepJson?.MSISDN || step?.StepJson?.MDN);

          if (stepWithNumber) {
            setPhoneNumber(stepWithNumber.StepJson.MSISDN || stepWithNumber.StepJson.MDN);
            setFetchingNumber(false);
            return;
          }

          if (currentAttempt < delays.length - 1) {
            currentAttempt++;
            const nextDelay = delays[currentAttempt];
            timeoutId = setTimeout(pollForNumber, nextDelay);
          } else {
            setFetchingNumber(false);
          }
        } catch (error) {
          Sentry.captureException(error);
          console.error(error);
          setFetchingNumber(false);
        }
      };

      timeoutId = setTimeout(pollForNumber, delays[0]);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [customerId, step, phoneNumber, orderId, fetchingNumber, makeApiRequest]);

  // Tracking
  useEffect(() => {
    const stepName = Object.entries(Steps).find(([, value]) => value === step)?.[0];
    if (stepName) {
      const eventName = `activation_${stepName.toLowerCase()}`;

      // GA tracking
      track(eventName, {
        userId: customerId,
      });

      // Clarity tracking
      if (window.clarity) {
        // eslint-disable-next-line no-undef
        clarity('set', 'page', 'Step ' + step);
        // eslint-disable-next-line no-undef
        clarity('set', 'step', eventName);
      }

      // Update History API
      history.replaceState({}, '', `/pages/activation?step=${stepName.toLowerCase()}`);

      // FB tracking
      if (window.fbq) {
        // eslint-disable-next-line no-undef
        fbq('trackCustom', eventName, {
          external_id: customerId,
        });
      }
    }

    // TripleWhale
    if (step === Steps.FINISHED && window.TriplePixel) {
      // eslint-disable-next-line no-undef
      TriplePixel('custom', 'activation');
    }
  }, [step]);

  const handleFetchedOrder = (data) => {
    console.log(data);
    setOrderId(data.orderId);
    setCustomerId(data.customerId);
    setRules(data.rules);

    // Returning to the activation flow after provisioning
    if (data.nextStep === Steps.CARD) {
      // setIsRetry(true);
    }

    setStep(data.nextStep);

    if (window.clarity) {
      window.clarity('identify', String(data.customerId));
    }

    Sentry.setUser({ id: data.customerId });
  };

  const handleTransferNumber = (instruction) => {
    track('activation_transfer_carrier', {
      userId: customerId,
      detail: instruction.title,
    });

    setNetworkInstructions(instruction);
    setStep(Steps.NUMBER_TRANSFER);
  };

  const handleTransferReturn = () => {
    setStep(Steps.NUMBER);
  };

  const handleNewNumber = (data) => {
    setProvisionData(data);
    setStep(Steps.CARD);
  };

  const handleInstallConfirm = () => {
    setStep(Steps.FINISHED);
  };

  const handleTransferStarted = (nextStep) => {
    track('activation_transfer_submited');
    setStep(nextStep);
  };

  // eSIM orders need the Install (QR) step; physical-SIM orders are done once activated.
  const completeActivation = () => {
    setStep(rules?.esim ? Steps.INSTALL : Steps.FINISHED);
  };

  const handleAccountActivated = () => {
    completeActivation();
  };

  const handlePortinFailed = (responseError) => {
    setPortinError(responseError);
    setStep(Steps.NUMBER_TRANSFER);
  };

  const handlePortinSubmitted = (nextStep) => {
    track('activation_portin_submited');
    setStep(nextStep);
  }

  const handleTransferSubmitted = () => {
    setStep(Steps.NUMBER_TRANSFER_STARTED);
  };

  const handleTransfer = (data) => {
    setProvisionData(data);
    setStep(Steps.CARD);
  };

  const handleTransferFinished = () => {
    setStatus((prev) => ({ ...prev, provisioned: true }));
    completeActivation();
  };

  const handleTransferIssue = (error) => {
    setTransferError(error);
    setStep(Steps.NUMBER_TRANSFER_ISSUE);
  };

  const handleNumberAllocation = (mdn) => {
    setPhoneNumber(mdn);
  };

  return html`
    <div class="flex flex-col items-center">
      <${ProgressBar} step=${progressStep} count=${count} />

      ${step === Steps.START && instantActivation &&
        html`<${StepInstantActivation} 
          code=${activationCode} 
          onFetchedOrder=${handleFetchedOrder}
          steps=${Steps}
          setStatus=${setStatus}
        />`}
      ${step === Steps.START && !instantActivation &&
        html`<${StepBegin} 
          onFetchedOrder=${handleFetchedOrder} 
          activationCode=${activationCode}
          steps=${Steps}
          setStatus=${setStatus}
        />`}
      ${step === Steps.NUMBER &&
        html`<${StepNumber}
          onSelectedTransfer=${handleTransferNumber}
          onSelectedNew=${handleNewNumber}
          customerId=${customerId}
          isPortinOnly=${rules?.isPortinOnly}
          isNewOnly=${rules?.isNewOnly}
        />`}
      ${step === Steps.NUMBER_TRANSFER &&
        html`<${StepNumberTransfer}
          onTransfer=${handleTransfer}
          onReturn=${handleTransferReturn}
          portinError=${portinError}
          instructions=${networkInstructions}
          customerId=${customerId}
        />`}
      ${step === Steps.NUMBER_TRANSFER_ISSUE &&
        html`<${StepNumberTransferIssue}
          onTransferStarted=${handleTransferStarted}
          onTransferSubmitted=${handleTransferSubmitted}
          customerId=${customerId}
          transferError=${transferError}
        />`}
      ${step === Steps.NUMBER_TRANSFER_STARTED &&
        html`<${StepTransferStarted}
          customerId=${customerId}
          onTransferFinished=${handleTransferFinished}
          onTransferIssue=${handleTransferIssue}
        />`}
      ${step === Steps.CARD &&
        html`<${StepCard}
          address=${rules?.address}
          provisionData=${provisionData}
          customerId=${customerId}
          onAccountActivated=${handleAccountActivated}
          onPortinFailed=${handlePortinFailed}
          onPortinSubmited=${handlePortinSubmitted}
          status=${status}
          setStatus=${setStatus}
        />`}
      ${step === Steps.INSTALL &&
        html`<${StepInstall}
          onInstallConfirm=${handleInstallConfirm}
          customerId=${customerId}
          onNumberAllocation=${handleNumberAllocation}
        />`}
      ${step === Steps.FINISHED && 
        html`<${StepFinished} 
          phoneNumber=${phoneNumber} 
        />`}
    </div>
  `;
};

export default Activation;
